import React, { useState } from 'react'
import { FaAngleDoubleDown, FaAngleDoubleUp } from 'react-icons/fa'

import SafetySystemsModal from './SafetySystemsModal'
import SensorInsights, { StatusBadge, displaySensorName, formatPrice } from './adasInsights'

const normalizeServiceDisplayName = (name) => {
  if (!name) return ''
  const rawName = String(name).trim()
  if (!rawName) return ''
  const stripped = rawName
    .replace(/\s*[-–]?\s*\(?(oem|aftermarket)\)?\s*$/i, '')
    .trim()
  return stripped || rawName
}

// Diagnostic scans carry an OEM/Aftermarket choice. normalizeServiceDisplayName
// strips any trailing "(OEM)/(Aftermarket)" from the name, so re-derive the scan
// type — prefer the explicit svc.scanType, else recover it from the raw operation
// name — and re-append it consistently as " (OEM)" / " (Aftermarket)".
const scanTypeSuffix = (svc) => {
  if (!svc) return ''
  let type = svc.scanType
  if (!type && svc.operation && svc.operation.name) {
    const match = String(svc.operation.name).match(/\(?(oem|aftermarket)\)?\s*$/i)
    if (match) type = match[1]
  }
  const normalized = String(type || '').toLowerCase()
  if (normalized === 'oem') return ' (OEM)'
  if (normalized === 'aftermarket') return ' (Aftermarket)'
  return ''
}

const AdasSections = ({ report, allowViewPricing }) => {
  // Insights default to expanded (#37249, "Expand all by default"). The nonce
  // is bumped on every Expand/Collapse All press so the broadcast re-applies
  // even when the value is unchanged — otherwise pressing Expand All a second
  // time, after collapsing a section by hand, would do nothing.
  const [expandAll, setExpandAll] = useState(true)
  const [expandNonce, setExpandNonce] = useState(0)
  const [showSafety, setShowSafety] = useState(false)
  const services = (report.adas && report.adas.services) || []

  const applyExpandAll = (next) => {
    setExpandAll(next)
    setExpandNonce(n => n + 1)
  }
  const sensorInsights = report.sensorInsights || []
  // Use the same collision flag as the admin portal: the editable, top-level
  // report field (admin toggles it via vehicleInvolvedInCollision), NOT the
  // VIN-decode hint under adas.vinDecodeData — the two can diverge.
  const collision = !!report.vehicleInvolvedInCollision
  const make = ((report.vehicle && report.vehicle.make) || '').toLowerCase()
  const collisionPhrase = make === 'ford' ? 'an accident' : 'a collision'

  if (!services.length) {
    return <p style={{ color: '#69787F', marginTop: 24 }}>No ADAS services on this report.</p>
  }

  // Group by categoryName, then by sensorName — matching admin's filter:
  // hide not-equipped sensors, and hide collision-only services when the
  // vehicle was not in a collision.
  const categories = {}
  services.forEach(svc => {
    if (svc.recommendedStatus === 'not-equipped') return
    if (svc.collisionRelated && !collision) return
    const cat = svc.categoryName || 'Uncategorized'
    if (!categories[cat]) categories[cat] = {}
    const sensor = svc.sensorName || 'General'
    if (!categories[cat][sensor]) categories[cat][sensor] = { services: [] }
    categories[cat][sensor].services.push(svc)
  })

  return (
    <div className='adas-detail__report-card'>
      <div className='adas-detail__report-header'>
        <div className='adas-detail__report-header-left'>
          <h3 className='adas-detail__report-title'>ADAS Report</h3>
          <label className='adas-detail__collision-check'>
            <input type='checkbox' checked={!!collision} readOnly />
            <span>Vehicle was involved in {collisionPhrase}</span>
          </label>
        </div>
        <div className='adas-detail__report-header-right'>
          <button className='adas-detail__btn-safety' onClick={() => setShowSafety(true)}>Safety Systems</button>
          {/* Expand/collapse all surfaced as icon buttons rather than buried in
              the kebab (#37249), matching the admin portal. The kebab held
              nothing else here, so it is removed rather than left with one
              item. */}
          <div className='adas-detail__insight-toggles'>
            <button
              type='button'
              className='adas-detail__insight-toggle'
              onClick={() => applyExpandAll(true)}
              title='Expand all insights'
              aria-label='Expand all insights'
            >
              <FaAngleDoubleDown />
            </button>
            <button
              type='button'
              className='adas-detail__insight-toggle'
              onClick={() => applyExpandAll(false)}
              title='Collapse all insights'
              aria-label='Collapse all insights'
            >
              <FaAngleDoubleUp />
            </button>
          </div>
        </div>
      </div>

      <div>
        {Object.entries(categories).map(([catName, sensors]) => (
          <div key={catName}>
            {Object.entries(sensors).map(([sensorName, { services: sensorServices }]) => (
              <div key={sensorName}>
                <div className='adas-detail__group-header'>
                  <span className='adas-detail__group-category'>{catName}</span>
                  <span className='adas-detail__group-divider'>|</span>
                  <span className='adas-detail__group-sensor'>{displaySensorName(sensorName)}</span>
                </div>

                {sensorServices.map(svc => {
                  const baseName = normalizeServiceDisplayName(svc.operation ? svc.operation.name : '')
                  const serviceName = baseName ? baseName + scanTypeSuffix(svc) : '—'
                  return (
                  <div key={svc._id} className='adas-detail__service-row'>
                    <div className='adas-detail__service-name'>
                      <span>{serviceName}</span>
                      {svc.calibrationType && (
                        <span className='calibration-type'>({svc.calibrationType})</span>
                      )}
                    </div>
                    <div className='adas-detail__service-status'>
                      <span className='adas-detail__meta-label'>Status:</span>
                      <StatusBadge status={svc.recommendedStatus} source={svc.recommendedSource} />
                    </div>
                    {allowViewPricing && (
                      <div className='adas-detail__service-price'>
                        <span className='adas-detail__meta-label'>Price:</span>
                        <span className='adas-detail__price-value'>{formatPrice(svc.amount)}</span>
                      </div>
                    )}
                    <span className='adas-detail__service-more'>⋯</span>
                  </div>
                  )
                })}

                <SensorInsights
                  sensorName={sensorName}
                  services={sensorServices}
                  sensorInsights={sensorInsights}
                  report={report}
                  collision={!!collision}
                  forceOpen={expandAll}
                  expandNonce={expandNonce}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      <SafetySystemsModal show={showSafety} onHide={() => setShowSafety(false)} report={report} />
    </div>
  )
}

export default AdasSections
